// Discovery Session notes on the Review: one per tools-map row and side card. Internal: staff only, never on a firm page.
// Autosaves 800 ms after typing stops and on blur; an emptied note is removed. Bold, lists and links show formatted.
// A saved note starts folded to one line (Internal note · its first words), so a full tools map stays short.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Lock, Plus } from 'lucide-react';
import { feedbackExcerpt, NOTE_MAX, type DiscoveryNote, type NoteSection } from '@upm/domain';
import { api } from '../lib/api';
import { firstName, fmtMonthDay, fmtTime } from '../lib/format';
import { Saver, type SaveStatus } from '../intake/saver';
import { Button, cx } from '../ui';
import { FormattedNote } from './FormattedNote';

/** The saved note (null once removed), so the Review keeps it across reloads and remounts. */
export type OnNoteSaved = (section: NoteSection, note: DiscoveryNote | null) => void;

export function AddNoteButton({ label, onClick, className }: { label: string; onClick: () => void; className?: string }) {
  return (
    <button type="button" onClick={onClick}
      className={cx('inline-flex min-h-8 items-center gap-1 border-0 bg-transparent p-0 text-[13px] font-semibold text-brand', className)}>
      <Plus aria-hidden="true" size={15} />Add internal note<span className="sr-only"> for {label}</span>
    </button>
  );
}

/** `label` names the section for screen readers. `onClose` runs when staff leave the box empty. */
export function NoteBox({ onboardingId, section, note, label, onSaved, autoFocus, onClose }: {
  onboardingId: string; section: NoteSection; note: DiscoveryNote | undefined; label: string; onSaved: OnNoteSaved;
  autoFocus?: boolean; onClose?: () => void;
}) {
  const [text, setText] = useState(note?.body ?? '');
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [fatal, setFatal] = useState<string | null>(null);
  const [open, setOpen] = useState(!note || !!autoFocus);
  const ref = useRef<HTMLDivElement>(null);
  const saver = useMemo(() => new Saver((e) => setFatal(e.message)), []);
  useEffect(() => {
    const off = saver.subscribe(setStatus);
    const warn = (e: BeforeUnloadEvent) => { if (saver.pending) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    // A row collapsing or the page changing still saves what was typed.
    return () => { off(); window.removeEventListener('beforeunload', warn); void saver.flush(); };
  }, [saver]);
  // A reload brings in another teammate's save; never while this one is being edited or its text failed to save.
  useEffect(() => {
    if (!ref.current?.contains(document.activeElement) && !saver.pending && !fatal) setText(note?.body ?? '');
  }, [note?.body, saver]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (body: string) => {
    setText(body);
    setFatal(null);
    saver.schedule(section, async () => {
      onSaved(section, (await api.put<DiscoveryNote | undefined>(`/api/admin/onboardings/${onboardingId}/notes/${section}`, { body })) ?? null);
    }, 800);
  };
  const state = fatal ?? (status === 'saving' ? 'Saving…'
    : status === 'error' ? 'Couldn’t save. Retrying…'
    : note ? `Saved by ${firstName(note.by)}, ${fmtMonthDay(note.at)} ${fmtTime(note.at)}` : '');
  if (!open && text.trim() && !fatal) {
    return (
      <div ref={ref}>
        <button type="button" aria-expanded="false" aria-label={`Show internal note for ${label}`} onClick={() => setOpen(true)}
          className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-line bg-row-hi px-2.5 py-1.5 text-left text-[13px] hover:bg-panel">
          <Lock aria-hidden="true" size={12} className="flex-none text-muted" />
          <span className="flex-none font-semibold text-ink-2">Internal note</span>
          <span className="w-0 min-w-0 flex-1 truncate text-muted">{feedbackExcerpt(text, 160)}</span>
          <ChevronDown aria-hidden="true" size={14} className="flex-none text-muted" />
        </button>
      </div>
    );
  }
  return (
    <div ref={ref} className="flex flex-col gap-1">
      <FormattedNote
        name={`internal note for ${label}`}
        label={(
          <span className="inline-flex flex-wrap items-center gap-x-1.5 text-[12px]">
            <span className="inline-flex items-center gap-1"><Lock aria-hidden="true" size={12} />Internal note<span className="sr-only"> for {label}.</span></span>
            <span className="font-normal text-muted">Staff only. The firm never sees this.</span>
          </span>
        )}
        autoFocus={autoFocus}
        maxLength={NOTE_MAX}
        placeholder="Notes from the Discovery Session"
        value={text}
        onChange={change}
        onDone={() => { void saver.flush(); if (!text.trim()) onClose?.(); }}
      />
      <div className="flex min-h-4 items-center justify-between gap-2">
        <div aria-live="polite" className={cx('text-[12px]', fatal || status === 'error' ? 'font-semibold text-bad' : 'text-muted')}>{state}</div>
        {text.trim() && !fatal && <Button variant="link" size="sm" className="!text-[12px]" aria-label={`Hide internal note for ${label}`} onClick={() => setOpen(false)}>Hide</Button>}
      </div>
    </div>
  );
}

/** A side card's note: an Add note link until there is one. */
export function CardNote({ onboardingId, section, note, label, onSaved }: {
  onboardingId: string; section: NoteSection; note: DiscoveryNote | undefined; label: string; onSaved: OnNoteSaved;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-line-soft pt-2.5">
      {note || open
        ? <NoteBox onboardingId={onboardingId} section={section} note={note} label={label} onSaved={onSaved} autoFocus={open && !note} onClose={() => setOpen(false)} />
        : <AddNoteButton label={label} onClick={() => setOpen(true)} />}
    </div>
  );
}
